import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React, { FunctionComponent, PropsWithChildren, useState } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { Transitions } from '~/assets/style/tokens'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { WithClassName } from '~/types/utils'
import { Icon, IconName } from './Icon'

const CollapsiblePanelSubjectTitle = styled.span`
  ${Fonts.H2}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  user-select: none;
  flex: 1;
  min-width: 0;
`

const CollapsibleTriggerIcon = styled(Icon)``

const CollapsibleTrigger = styled.button<{ opened?: boolean }>`
  ${Fonts.P2}
  ${Fonts.colors.King}
  font-weight: bold;
  cursor: pointer;
  background: none;
  border: none;
  display: inline-flex;
  align-items: center;

  :hover {
    ${Fonts.colors.Cobalt}
  }

  & ${CollapsibleTriggerIcon} {
    transition: transform ${Transitions.quick};
    transform: rotate(${({ opened }) => (opened ? '-180deg' : '0')});
  }
`

const CollapsiblePanelSubjectActions = styled.div`
  display: flex;
  flex-shrink: 0;
`
const CollapsiblePanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const CollapsiblePanelWrapper = styled.div<{ isClickable?: boolean; isOpen: boolean }>`
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  background-color: ${Colors.White};
  display: flex;
  flex-direction: column;
  border: 1px solid ${Colors.Platinum};
  border-radius: 4px;
  padding: 16px 12px;

  ${({ isOpen }) =>
    isOpen &&
    css`
      padding-bottom: 0;
    `}
`

const CollapsibleContent = styled.div<{ isOpen: boolean; hasBorderWhenOpened?: boolean }>`
  cursor: auto;
  padding: 12px 0;

  ${({ isOpen }) =>
    !isOpen &&
    css`
      display: none;
    `}

  ${({ isOpen, hasBorderWhenOpened }) =>
    isOpen &&
    hasBorderWhenOpened &&
    css`
      margin-top: 16px;
      border-top: 1px solid ${Colors.Platinum};
    `}
`

const COLLAPSIBLE_STATE = {
  open: 'open',
  closed: 'closed',
}

type CollapsibleProps = WithClassName & {
  title: React.ReactNode
  icon?: IconName
  actions?: React.ReactNode
  isCollapsible?: { onToggle?: (wantToOpen: boolean) => void; trackingLabel: TrackingLabel }
  forceState?: keyof typeof COLLAPSIBLE_STATE
  hideCollapseIcon?: boolean
  defaultState?: keyof typeof COLLAPSIBLE_STATE
  canHideChildren?: boolean
  hasBorderWhenOpened?: boolean
}

const BaseCollapsiblePanel: FunctionComponent<PropsWithChildren<CollapsibleProps>> = ({
  className,
  children,
  isCollapsible,
  forceState,
  actions,
  title,
  icon,
  hideCollapseIcon,
  defaultState,
  canHideChildren,
  hasBorderWhenOpened,
}) => {
  // defaultState wins over forceState on mount, matching the order the two effects used to run in
  const [isOpen, setIsOpen] = useState(defaultState !== undefined ? defaultState === COLLAPSIBLE_STATE.open : forceState === COLLAPSIBLE_STATE.open)
  const [previousForceState, setPreviousForceState] = useState(forceState)
  const [previousDefaultState, setPreviousDefaultState] = useState(defaultState)

  // forceState and defaultState are the parent's way to drive the panel: realign on change, during render rather than in an effect
  if (previousForceState !== forceState) {
    setPreviousForceState(forceState)
    setIsOpen(forceState === COLLAPSIBLE_STATE.open)
  }

  if (previousDefaultState !== defaultState) {
    setPreviousDefaultState(defaultState)
    if (defaultState !== undefined) {
      setIsOpen(defaultState === COLLAPSIBLE_STATE.open)
    }
  }

  const toggle = () => {
    isCollapsible?.onToggle && isCollapsible.onToggle(!isOpen)
    setIsOpen(!isOpen)
  }

  return (
    <CollapsiblePanelWrapper className={className} isClickable={!!isCollapsible && !forceState} isOpen={isOpen} data-tracking={isCollapsible?.trackingLabel}>
      <CollapsiblePanelHeader
        onClick={(event) => {
          event.preventDefault()
          !forceState && isCollapsible && toggle()
        }}
      >
        <CollapsiblePanelSubjectTitle>
          {icon && <Icon name={icon} width={'22px'} />} {title}
        </CollapsiblePanelSubjectTitle>
        <CollapsiblePanelSubjectActions>
          {actions}
          {isCollapsible && !hideCollapseIcon && (
            <CollapsibleTrigger opened={isOpen}>
              <CollapsibleTriggerIcon width={'24px'} name={'chevron_down'} />
            </CollapsibleTrigger>
          )}
        </CollapsiblePanelSubjectActions>
      </CollapsiblePanelHeader>
      {((isCollapsible && (isOpen || canHideChildren)) || (!isCollapsible && children)) && (
        <CollapsibleContent hasBorderWhenOpened={hasBorderWhenOpened} isOpen={isOpen || (!isCollapsible && !!children && !canHideChildren && forceState !== COLLAPSIBLE_STATE.closed)}>
          {children}
        </CollapsibleContent>
      )}
    </CollapsiblePanelWrapper>
  )
}

export const CollapsiblePanel = styled(BaseCollapsiblePanel)``
